import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Search } from 'lucide-react'
import { api } from '@/lib/api'
import type { AdminUserSummary, Page } from '@/lib/types'
import { PageHeader } from '@/components/AppLayout'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatDate, formatRelative } from '@/lib/utils'
import { ErrorNote } from './DashboardPage'

export function UsersPage() {
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<string>('')
  const [page, setPage] = useState(0)

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', query, status, page],
    queryFn: () =>
      api.get<Page<AdminUserSummary>>('api/admin/users', {
        q: query || undefined,
        status: status || undefined,
        page,
        size: 25,
      }),
  })

  return (
    <>
      <PageHeader title="Users" description="Search by email, name or Keycloak subject." />

      <div className="space-y-4 p-8">
        {error && <ErrorNote message={(error as Error).message} />}

        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            // Searching resets to the first page; keeping the old offset would
            // show an empty result set for a narrower query.
            setPage(0)
            setQuery(search.trim())
          }}
        >
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
            <Input
              className="pl-9"
              placeholder="Search users…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="h-9 rounded-lg border bg-[var(--color-surface)] px-3 text-sm"
            value={status}
            onChange={(e) => {
              setPage(0)
              setStatus(e.target.value)
            }}
          >
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="DISABLED">Disabled</option>
          </select>
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>

        <div className="rounded-[var(--radius-card)] border bg-[var(--color-surface)]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Onboarded</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Last seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-[var(--color-text-tertiary)]">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {data?.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-[var(--color-text-tertiary)]">
                    No users match that search.
                  </TableCell>
                </TableRow>
              )}
              {data?.items.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Link to={`/users/${user.id}`} className="block hover:underline">
                      <span className="font-medium">{user.displayName ?? user.email ?? 'Unnamed'}</span>
                      <span className="block text-xs text-[var(--color-text-tertiary)]">
                        {user.email ?? user.subject}
                      </span>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.status === 'ACTIVE' ? 'success' : 'danger'}>{user.status}</Badge>
                  </TableCell>
                  <TableCell className="text-[var(--color-text-secondary)]">
                    {user.onboardingCompleted ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell className="text-[var(--color-text-secondary)]">{formatDate(user.createdAt)}</TableCell>
                  <TableCell className="text-[var(--color-text-secondary)]">
                    {formatRelative(user.lastSeenAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <Pagination
          page={data?.page ?? 0}
          totalPages={data?.totalPages ?? 0}
          totalItems={data?.totalItems ?? 0}
          onChange={setPage}
        />
      </div>
    </>
  )
}

export function Pagination({
  page,
  totalPages,
  totalItems,
  onChange,
}: {
  page: number
  totalPages: number
  totalItems: number
  onChange: (page: number) => void
}) {
  return (
    <div className="flex items-center justify-between text-sm text-[var(--color-text-secondary)]">
      <span>
        {totalItems} result{totalItems === 1 ? '' : 's'}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" disabled={page <= 0} onClick={() => onChange(page - 1)}>
          Previous
        </Button>
        <span className="tabular">
          {totalPages === 0 ? 0 : page + 1} / {totalPages}
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={page + 1 >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
